import { useCallback, useContext } from "react"

import { useLocalSearchParams } from "expo-router"

import {
  AccountCollection,
  AccountData,
  ClientCollection,
  EstimatedAccountValue,
  EstimatedAccountValueCollection,
} from "wealthmode-frontend/backend"
import { Box, HStack } from "wealthmode-frontend/gluestack/ui"
import { formatMoney } from "wealthmode-frontend/util"

import { DataTable } from "@/components"
import { BackendContext } from "@/context"

export default function EstimatedAccountValues() {
  const { client_id, account_id } = useLocalSearchParams<{
    client_id: string
    account_id: string
  }>()

  const backend = useContext(BackendContext)

  const load = useCallback(async () => {
    const clients = backend.collection("clients") as ClientCollection
    const client = clients.model(client_id)
    await client.fetch({ reload: true })

    const accounts = client.link("accounts")?.resolve() as AccountCollection
    const account = accounts.model(account_id)
    await account.fetch({ reload: true })

    const data = account.embed("data") as AccountData
    const values = data.link("estimated_account_values")?.resolve() as EstimatedAccountValueCollection | undefined
    if (!values) {
      return undefined
    }

    await values.fetch({ reload: true })

    return values
  }, [backend, client_id, account_id])

  return (
    <Box>
      <HStack>
        <Box className="flex-1">
          <DataTable
            title={"Estimated Account Values"}
            load={load}
            header={[
              { id: "date", title: "Date" },
              { id: "value", title: "Value" },
              { id: "cash", title: "Cash" },
              { id: "priced_value", title: "Priced" },
              { id: "unpriced_value", title: "Unpriced" },
            ]}
            render={(model: EstimatedAccountValue, field: string) => {
              switch (field) {
                case "value":
                case "cash":
                case "priced_value":
                case "unpriced_value":
                  return formatMoney(model.get(field))
              }
              return model.get(field)
            }}
          />
        </Box>
      </HStack>
    </Box>
  )
}
